import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { Search, X, ListTodo, Building2, Activity, Users, Loader2 } from 'lucide-react';

const moduleIcons = {
  tasks: ListTodo,
  customers: Building2,
  activities: Activity,
  users: Users,
};
const moduleColors = {
  tasks: 'bg-blue-100 text-blue-700',
  customers: 'bg-purple-100 text-purple-700',
  activities: 'bg-green-100 text-green-700',
  users: 'bg-orange-100 text-orange-700',
};

export function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get('/search', { params: { q, limit: 15 } });
      setResults(res.data.results || []);
    } catch { setResults([]); }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  useEffect(() => {
    const handle = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ctrl+K shortcut
  useEffect(() => {
    const handle = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setIsOpen(true); }
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (result) => {
    setIsOpen(false); setQuery('');
    navigate(result.link);
  };

  return (
    <div className="relative" ref={panelRef}>
      <div className="relative" onClick={() => setIsOpen(true)}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          placeholder="Search everything... (Ctrl+K)"
          className="pl-9 pr-8 h-9 bg-gray-50 border-gray-200 focus:bg-white text-sm w-full"
          data-testid="global-search-input"
        />
        {query && (
          <button onClick={(e) => { e.stopPropagation(); setQuery(''); setResults([]); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen && query.length >= 2 && (
        <div className="absolute left-0 right-0 top-11 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[60vh] overflow-y-auto" data-testid="search-results-panel">
          {loading ? (
            <div className="p-4 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">No results for "{query}"</div>
          ) : (
            <div className="py-1">
              {results.map((r, i) => {
                const Icon = moduleIcons[r.module] || Search;
                return (
                  <div key={`${r.module}-${r.id}-${i}`}
                    onClick={() => handleSelect(r)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors">
                    <div className={`p-2 rounded-lg ${moduleColors[r.module] || 'bg-gray-100'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                      <p className="text-xs text-gray-500 truncate">{r.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {r.priority && (
                        <Badge className={`text-xs ${r.priority === 'high' ? 'bg-red-100 text-red-700' : r.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                          {r.priority}
                        </Badge>
                      )}
                      {r.status && (
                        <Badge variant="outline" className="text-xs capitalize">{r.status}</Badge>
                      )}
                      <Badge className={`text-xs ${moduleColors[r.module] || 'bg-gray-100'}`}>{r.module}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
